import { cx } from '@emotion/css';
import { cn } from '@phoenix/design-system/frontend/util/classNameUtils';
import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, ReactElement, useState, useId } from 'react';

const iconArrowAccordion = getDesignSystemIcon('icon-arrow-accordion.svg');

export interface AccordionProps {
  title?: ReactElement | string;
  defaultIsOpen?: boolean;
  header?: React.ReactNode;
  headerClassName?: string;
  contentPadding?: string;
  className?: string;
  'data-testid'?: string;
  isOpen?: boolean;
}

export const Accordion: FC<React.PropsWithChildren<AccordionProps>> = ({
  defaultIsOpen = false,
  title,
  header,
  children,
  headerClassName,
  contentPadding,
  className,
  'data-testid': dataTestId,
  isOpen,
}) => {
  // By using `AnimatePresence` to mount and unmount the contents, we can animate
  // them in and out while also only rendering the contents of open accordions

  const [_isOpen, setIsOpen] = useState(defaultIsOpen);
  const [interacted, setInteracted] = useState(false);
  const isOpenCombined = interacted ? _isOpen : isOpen || _isOpen;
  const handleSetIsOpen = (shouldOpen: boolean) => {
    setInteracted(true);
    setIsOpen(shouldOpen);
  };

  // Generate unique IDs for accessibility
  const baseId = useId();
  const headerId = `accordion-header-${baseId}`;
  const panelId = `accordion-panel-${baseId}`;

  return (
    <div className={className && cx(className)} data-testid={dataTestId}>
      <AnimatePresence initial={false}>
        <motion.button
          initial={false}
          data-testid="accordionToggle"
          className={cn(
            'peer w-full cursor-pointer text-left',
            'focus-visible:outline-offset-[4px] focus-visible:outline-[#252525] focus-visible:outline-[2px] focus-visible:outline'
          )}
          onClick={() => handleSetIsOpen(!isOpenCombined)}
          type="button"
          aria-expanded={isOpenCombined}
          aria-controls={panelId}
          id={headerId}
        >
          <div
            className={cx(
              'flex flex-row justify-between py-4 hover:border-b-primary-black',
              {
                'border-b-2 border-secondary-mid': !isOpenCombined,
              },
              contentPadding,
              headerClassName
            )}
          >
            {header ? <>{header}</> : <div className="flex stihl-text-h3-desktop">{title}</div>}
            <div className="relative mr-2 flex items-center">
              <motion.img
                src={iconArrowAccordion}
                alt="plus"
                className="h-4 w-4"
                variants={{
                  open: { rotate: 180 },
                  closed: { rotate: 0 },
                }}
                animate={isOpenCombined ? 'open' : 'closed'}
                transition={{ bounce: 0 }}
              />
            </div>
          </div>
        </motion.button>
        {isOpenCombined && (
          <motion.section
            className="border-b-2 border-secondary-mid peer-hover:border-b-primary-black"
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className={cx('pb-6 pt-4', contentPadding)}>{children}</div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};
